import { announce } from "@kovernow/flows/announce";
import { SUPPORT_MESSAGE_MAX, SUPPORT_SUBJECT_MAX, supportQueryKeys, validateHelpMessage } from "@kovernow/flows/support";
import { Button } from "@kovernow/react-ui/components/button";
import { InfoBanner } from "@kovernow/react-ui/components/info-banner";
import { Label } from "@kovernow/react-ui/components/label";
import { Skeleton } from "@kovernow/react-ui/components/skeleton";
import { Textarea } from "@kovernow/react-ui/components/textarea";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { FormField } from "../../components/FormField";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";

/**
 * W-8 "Need help verifying?" (HK): a short message form that opens an
 * `identity_verification` support case linked to the verification reference
 * (when there is one), then "We're arranging help — case {ref}". With the
 * `support_case_intake` flag off: a mailto to the support address with the
 * reference in the subject. The stored `returnTo` is left alone.
 *
 * Controlled: the screen owns `open` (so a RecoveryView action can open it)
 * and the sent case ref (so the confirmation survives the screen switching
 * branches, which remounts this component).
 */
export function NeedHelpVerifying({
  open,
  onOpen,
  onClose,
  doneRef,
  onDone,
  reference,
  showTrigger = true,
}: {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** The case ref once sent; the confirmation replaces the trigger. */
  doneRef: string | null;
  onDone: (ref: string) => void;
  /** The ShuftiPro reference this tab knows about (never identity data). */
  reference?: string;
  /** false when a RecoveryView already offers the action. */
  showTrigger?: boolean;
}) {
  const t = useT();

  if (doneRef) {
    return (
      <InfoBanner
        role="status"
        tone="info"
        title={t("verify.hk.help.done", { ref: doneRef })}
        body={t("verify.hk.help.doneBody")}
        data-testid="verify-help-done"
      />
    );
  }
  if (!open) {
    return showTrigger ? (
      <div>
        <Button type="button" variant="link" size="touch" className="px-0" onClick={onOpen} data-testid="verify-help">
          {t("verify.hk.help.button")}
        </Button>
      </div>
    ) : null;
  }
  return <HelpPanel reference={reference} onClose={onClose} onDone={onDone} />;
}

function HelpPanel({
  reference,
  onClose,
  onDone,
}: {
  reference?: string;
  onClose: () => void;
  onDone: (ref: string) => void;
}) {
  const t = useT();
  const id = useId();
  const { supportApi, config } = useServices();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const flags = useQuery({
    queryKey: supportQueryKeys.flags(),
    queryFn: () => supportApi.intakeEnabled(),
    staleTime: 60_000,
    retry: false,
  });
  const [subject, setSubject] = useState(() => t("verify.hk.help.subjectDefault"));
  const [message, setMessage] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const sendingRef = useRef(false);
  // One key per opened form: a retried send replays the same case.
  const [idempotencyKey] = useState(() => globalThis.crypto.randomUUID());

  useEffect(() => headingRef.current?.focus(), []);

  const messageError = showErrors ? validateHelpMessage(message) : undefined;
  const subjectError = showErrors && !subject.trim() ? "verify.hk.help.validation.subjectRequired" : undefined;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (validateHelpMessage(message) || !subject.trim() || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setFailed(false);
    try {
      const { ref } = await supportApi.createSupportCase(
        {
          caseType: "identity_verification",
          subject: subject.slice(0, SUPPORT_SUBJECT_MAX),
          message,
          ...(reference ? { verificationReference: reference } : {}),
        },
        idempotencyKey,
      );
      announce(t("verify.hk.help.done", { ref }));
      onDone(ref);
      // Close in the parent too: a later branch change must not reopen the form (new idempotency key).
      onClose();
    } catch {
      setFailed(true);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const headingId = `${id}-title`;
  let content;
  if (flags.isPending) {
    content = (
      <div aria-busy="true" data-testid="verify-help-loading">
        <span className="sr-only">{t("common.loading")}</span>
        <Skeleton className="h-24 w-full" />
      </div>
    );
  } else if (flags.data !== true) {
    // Flag off (or unreadable): email support with the reference in the subject.
    const subjectLine = reference
      ? t("verify.hk.help.mailtoSubject", { ref: reference })
      : t("verify.hk.help.mailtoSubjectNoRef");
    content = (
      <div className="flex flex-col gap-3" data-testid="verify-help-mailto">
        <p>{t("verify.hk.help.mailtoBody")}</p>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="default" size="touch">
            <a href={`mailto:${config.brand.supportEmail}?subject=${encodeURIComponent(subjectLine)}`}>
              {t("verify.hk.help.mailtoAction")}
            </a>
          </Button>
          <Button type="button" variant="outline" size="touch" onClick={onClose}>
            {t("verify.hk.help.cancel")}
          </Button>
        </div>
      </div>
    );
  } else {
    const messageId = `${id}-message`;
    const messageErrorId = `${id}-message-error`;
    content = (
      <form noValidate onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" data-testid="verify-help-form">
        <p className="text-sm text-muted-foreground">{t("verify.hk.help.intro")}</p>
        <FormField
          label={t("verify.hk.help.subjectLabel")}
          value={subject}
          maxLength={SUPPORT_SUBJECT_MAX}
          required
          aria-required
          onChange={(e) => setSubject(e.target.value)}
          error={subjectError ? t(subjectError) : undefined}
          data-testid="verify-help-subject"
        />
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={messageId}>{t("verify.hk.help.messageLabel")}</Label>
          <Textarea
            id={messageId}
            value={message}
            rows={4}
            required
            aria-required
            aria-invalid={messageError ? true : undefined}
            aria-describedby={messageError ? messageErrorId : undefined}
            onChange={(e) => setMessage(e.target.value)}
            className="min-h-24"
            maxLength={SUPPORT_MESSAGE_MAX}
            data-testid="verify-help-message"
          />
          {messageError ? (
            <p id={messageErrorId} className="text-sm font-semibold text-status-error">
              {t(messageError)}
            </p>
          ) : null}
        </div>
        {failed ? (
          <InfoBanner role="alert" tone="warning" title={t("verify.hk.help.failed")} data-testid="verify-help-failed" />
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            size="touch"
            variant="cta"
            aria-busy={sending || undefined}
            aria-disabled={sending || undefined}
            data-testid="verify-help-send"
          >
            {sending ? t("verify.hk.help.sending") : t("verify.hk.help.send")}
          </Button>
          <Button type="button" variant="outline" size="touch" onClick={onClose}>
            {t("verify.hk.help.cancel")}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-3 rounded-lg border p-4"
      data-testid="verify-help-panel"
    >
      <h2 id={headingId} ref={headingRef} tabIndex={-1} className="text-lg font-semibold">
        {t("verify.hk.help.title")}
      </h2>
      {content}
    </section>
  );
}
